import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn, type TranslationKey } from "@racoon/i18n";
import { normalizeForSearch } from "@racoon/lib";
import { JOB_TYPE_CATEGORY_KEY, JOB_TYPE_CATEGORY_ORDER } from "~/shared/lib/translationMaps";

export type JobTypeOption = {
  type: ConnectProto.JobType;
  /** The key a tariff is stored under — `JOB_TYPE_<enum name>`. */
  tariffKey: string;
  label: string;
  /** Localised category ("СО", "ТБО", …); empty for uncategorised types. */
  category: string;
};

const ALL_JOB_TYPES = (
  Object.values(ConnectProto.JobType).filter((v) => typeof v === "number") as ConnectProto.JobType[]
).filter((v) => v !== 0);

/**
 * Every job type as a tariff-picker option, ordered so the categories read as
 * blocks ("СО / ТБО / Дезинфекция / Мультилифт / ЖБО", uncategorised last).
 * Shared by the customer card's contract form and the onboarding wizard.
 */
export function useJobTypeOptions(t: TFn) {
  const options = useMemo<JobTypeOption[]>(() => {
    const rank = (categoryKey?: string) => {
      const index = categoryKey ? JOB_TYPE_CATEGORY_ORDER.indexOf(categoryKey) : -1;
      return index === -1 ? JOB_TYPE_CATEGORY_ORDER.length : index;
    };

    return ALL_JOB_TYPES.map((type) => {
      const tariffKey = `JOB_TYPE_${ConnectProto.JobType[type]}`;
      const categoryKey = JOB_TYPE_CATEGORY_KEY[type];
      const rawLabel = t(Namespace.Enum, tariffKey as TranslationKey);
      return {
        type,
        tariffKey,
        // The category is shown beside the label, so the "(мультилифт)" suffix the
        // enum label carries is dropped — only for categorised labels, so unrelated
        // parentheticals (e.g. "Перевозка (МПЗ - МПЗ)") stay intact.
        label: categoryKey ? rawLabel.replace(/\s*\([^)]*\)\s*$/, "").trim() : rawLabel,
        category: categoryKey ? t(Namespace.Enum, categoryKey as TranslationKey) : "",
        rank: rank(categoryKey),
      };
    })
      .sort((a, b) => a.rank - b.rank)
      .map(({ rank: _rank, ...option }) => option);
  }, [t]);

  /** Lookup by tariff key, so a priced row can show the same label and category. */
  const byKey = useMemo(
    () => new Map(options.map((option) => [option.tariffKey, option])),
    [options],
  );

  return { options, byKey };
}

/** Case- and layout-insensitive match on the label or the category. */
export function filterJobTypeOptions(options: JobTypeOption[], query: string): JobTypeOption[] {
  const needle = normalizeForSearch(query);
  if (!needle) return options;
  return options.filter(
    (option) =>
      normalizeForSearch(option.label).includes(needle) ||
      normalizeForSearch(option.category).includes(needle),
  );
}
